import { html, LitElement, nothing, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import { customElement, property } from 'lit/decorators.js';
import { WEB_COMPONENT_FINDING_DETAILS } from '../../../../webcomponents.constants';
import { translateFn } from '../../../../utils/translator.helper';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import {
  EASMActions,
  FINDING_DETAILS_BY_TYPE,
  FindingDetailsPayload,
  FindingGroup,
  NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK,
} from '../types';
import { SelectEvents } from '../../../../ui/gzSelect/types';
import { copyToClipboard } from '../../../../utils/htmlElement.utils';
import { createRows } from '../../utils';
import { ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { COMPLEX_SEARCH_FILTERS } from '../../../../edr/incidentsGrid/types';
import { EASM_SMARTVIEWS_LABELS, FindingClassType, FindingType, UNAVAILABLE_DATA } from '../../../types';
import { EasmSideMenuEvents } from '../../../easmSideMenu/types';
import { assigneeRenderer, generateFiltersForInventory } from '../../../utils/utils';
import { FilterType } from '../../../widgets/genericTopData/types/types';

@customElement(WEB_COMPONENT_FINDING_DETAILS)
export class FindingDetails extends LitElement {
  @property({
    type: Object,
    reflect: true,
  })
  payload: FindingDetailsPayload;

  @property({
    type: Number,
    reflect: true,
  })
  dependencies: any;

  private numberOfRelatedAssetsDisplayed = NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
  private numberOfArtifactsDisplayed = NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
  private translator = BaseWebComponentTranslator.getInstance();

  static get styles() {
    return styles;
  }

  constructor() {
    super();
  }

  /**
   * Shows more related assets.
   */
  private showMoreRelatedAssets(): void {
    this.numberOfRelatedAssetsDisplayed += NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
    this.requestUpdate();
  }

  /**
   * Shows more artifacts.
   */
  private showMoreArtifacts(): void {
    this.numberOfArtifactsDisplayed += NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
    this.requestUpdate();
  }

  /**
   * Shows less related assets.
   */
  private showLessRelatedAssets(): void {
    this.numberOfRelatedAssetsDisplayed = NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
    this.requestUpdate();
  }

  /**
   * Shows fewer artifacts.
   */
  private showLessArtifacts(): void {
    this.numberOfArtifactsDisplayed = NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK;
    this.requestUpdate();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener(SelectEvents.SELECTION_CHANGED, this.onSelectionChanged);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(SelectEvents.SELECTION_CHANGED, this.onSelectionChanged);
  }

  /**
   * Retrieves general information by type.
   * @param type - The finding class type.
   * @returns An array of general finding information objects.
   */
  getGeneralInfoByType(type: FindingClassType): Object[] {
    const payloadType = this.payload.type as unknown as FindingType;
    const typeTranslations = BaseWebComponentTranslator.getInstance().translateEASMType(payloadType);
    const returnArrObjs: Object[] = [];

    if (type === FindingClassType.ASSET) {
      returnArrObjs.push(
        {
          label: translateFn('Asset type'),
          value: typeTranslations,
        },
        {
          label: translateFn('Asset status'),
          value: BaseWebComponentTranslator.getInstance().translateNextScanInclusionStatus(this.payload.nextScanStatus),
        }
      );
    } else if (type === FindingClassType.ARTIFACT) {
      returnArrObjs.push({
        label: translateFn('Artifact type'),
        value: typeTranslations,
      });
    }

    returnArrObjs.push(
      {
        label: translateFn('Investigation status'),
        value: this.translator.translateInvestigationStatus(this.payload.metadata.investigation.status),
      },
      {
        label: translateFn('Assignee'),
        value: assigneeRenderer(this.payload.assignee, this.dependencies.getUser().company.id, this.translator),
      },
      {
        label: translateFn('Priority'),
        value: this.translator.translateInvestigationPriority(this.payload.metadata.investigation.priority),
      },
      {
        label: translateFn('Notes'),
        value: 0 === this.payload.metadata.investigation.notesCount
          ? translateFn('Add note')
          : this.payload.metadata.investigation.notesCount,
        callback: (): void => {
          this.dispatchEvent(
            new CustomEvent(
              EasmSideMenuEvents.OPEN_NOTES,
              { detail: { findingId: this.payload._id, isBackButtonEnabled: true }, bubbles: true, composed: true }
            )
          );
        },
      }
    );

    if (this.payload.companyName) {
      returnArrObjs.push({
        label: translateFn('Company'),
        value: this.payload.companyName,
      });
    }

    return returnArrObjs;
  }

  render() {
    let findingSpecificDetails: unknown = nothing;
    let findingSpecificBottomSection: unknown = nothing;
    const findingSpecificDetailsClass = FINDING_DETAILS_BY_TYPE[this.payload.type];
    if (findingSpecificDetailsClass) {
      findingSpecificDetails = findingSpecificDetailsClass.getGeneralInfo(this.payload, this.dependencies);
      findingSpecificBottomSection = findingSpecificDetailsClass.getGeneralInfoBottom(this.payload, this.dependencies);
    }

    return html`
      <div class="container">
        <gz-title class="section-title" fontSize="11">${translateFn('General')}</gz-title>
        ${createRows(this.getGeneralInfoByType(this.payload.class), UNAVAILABLE_DATA)} ${findingSpecificDetails}
        ${this.renderRelatedAssetsSection()} ${this.renderArtifactsSection()} ${findingSpecificBottomSection}
      </div>
    `;
  }

  /**
   * Injects dependencies into the component.
   * @param dependencies - The dependencies to inject.
   */
  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  /**
   * Renders the related assets body.
   * @param relatedAssetsItems - The related assets items.
   * @param relatedAssetsObjects - The related assets objects.
   * @returns The related assets body template.
   */
  private renderRelatedAssetsBody(
    relatedAssetsItems: TemplateResult[],
    relatedAssetsObjects: FindingGroup[]
  ): TemplateResult {
    if (relatedAssetsObjects.length === 0) {
      return html`<gz-text fontSize="14" class="text">${translateFn('No related assets found')}</gz-text>`;
    }
    return html` <gz-labeled-content label="${translateFn('Related assets')}" class="labeled-row" labelFontSize="14">
        ${relatedAssetsItems}
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          class="show-more-related-findings ${relatedAssetsItems.length < relatedAssetsObjects.length ? '' : 'hidden'}"
          @click="${this.showMoreRelatedAssets}"
        >
          ${translateFn('Show more')}
        </gz-button>
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          class="show-less-related-findings ${relatedAssetsItems.length >
            NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK && relatedAssetsItems.length >= relatedAssetsObjects.length
            ? ''
            : 'hidden'}"
          @click="${this.showLessRelatedAssets}"
        >
          ${translateFn('Show less')}
        </gz-button>
      </gz-labeled-content>

      <div class="row">
        <gz-button
          id="view-related-assets"
          type="${ButtonTypes.OUTLINE}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          @click="${this.dispatchFilterRelatedAsset.bind(this)}"
        >
          ${translateFn('View related assets')}
        </gz-button>
      </div>`;
  }

  /**
   * Renders the related assets section.
   * @returns The related assets section template or nothing.
   */
  private renderRelatedAssetsSection(): TemplateResult | symbol {
    if (!this.payload.groupedByType) {
      return html`${nothing}`;
    }
    const relatedAssetsObjects = this.payload.groupedByType.assets;
    const relatedAssetsItems = [] as TemplateResult[];
    for (
      let indexOfItem = 0;
      indexOfItem < this.numberOfRelatedAssetsDisplayed && indexOfItem < relatedAssetsObjects.length;
      indexOfItem++
    ) {
      relatedAssetsItems.push(this.createRelatedFindingRow(relatedAssetsObjects[indexOfItem], FindingClassType.ASSET));
    }
    const total = relatedAssetsObjects.reduce((acc, item) => acc + item.items.length, 0);

    return html`
      <div class="section-title fs-11 bordered">${translateFn('Related assets ({total})', { total: total })}</div>
      ${this.renderRelatedAssetsBody(relatedAssetsItems, relatedAssetsObjects)}
    `;
  }

  /**
   * Dispatches filter for related asset.
   */
  private async dispatchFilterRelatedAsset() {
    let filterType = FilterType.ASSET_RELATED_TO_ASSET;
    if (this.payload.class === FindingClassType.ARTIFACT) {
      filterType = FilterType.ASSETS_RELATED_TO_ARTIFACT;
    }
    const filtersToSend = generateFiltersForInventory(
      filterType,
      [
        {
          label: this.payload.name,
          value: this.payload.name,
        },
      ],
      this.payload.companyId,
      [this.payload.uniqueIdentifier]
    );
    await NavigationHelper.navigateToEASMAssetsInventory({
      filters: filtersToSend,
      smartViewLabel: EASM_SMARTVIEWS_LABELS.ALL_ASSETS,
    });
    this.dispatchEvent(
      new CustomEvent(EasmSideMenuEvents.CLOSE_SIDE_MENU, {
        detail: { preventGridRefresh: true },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Dispatches filter for related artifact.
   */
  private async dispatchFilterRelatedArtifact() {
    const filtersToSend = generateFiltersForInventory(
      FilterType.ARTIFACTS_RELATED_TO_ASSET,
      [
        {
          label: this.payload.name,
          value: this.payload.name,
        },
      ],
      this.payload.companyId,
      [this.payload.uniqueIdentifier]
    );
    await NavigationHelper.navigateToEASMArtifactsInventory({
      filters: filtersToSend,
      smartViewLabel: EASM_SMARTVIEWS_LABELS.ALL_ARTIFACTS,
    });
    this.dispatchEvent(
      new CustomEvent(EasmSideMenuEvents.CLOSE_SIDE_MENU, {
        detail: { preventGridRefresh: true },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Renders the related artifacts body.
   * @param relatedArtifactsItems - The related artifacts items.
   * @param relatedArtifactsObjects - The related artifacts objects.
   * @returns The related artifacts body template.
   */
  private renderRelatedArtifactsBody(
    relatedArtifactsItems: TemplateResult[],
    relatedArtifactsObjects: FindingGroup[]
  ): TemplateResult {
    if (relatedArtifactsObjects.length === 0) {
      return html`<gz-text fontSize="14" class="text">${translateFn('No related artifacts found')}</gz-text>`;
    }
    return html` <gz-labeled-content label="${translateFn('Artifacts')}" class="labeled-row" labelFontSize="14">
        ${relatedArtifactsItems}
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          class="show-more-related-findings ${relatedArtifactsItems.length < relatedArtifactsObjects.length
            ? ''
            : 'hidden'}"
          @click="${this.showMoreArtifacts}"
        >
          ${translateFn('Show more')}
        </gz-button>
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          class="show-less-related-findings ${relatedArtifactsItems.length >
            NUMBER_OF_RELATED_FINDINGS_TO_DISPLAY_IN_BULK &&
          relatedArtifactsItems.length >= relatedArtifactsObjects.length
            ? ''
            : 'hidden'}"
          @click="${this.showLessArtifacts}"
        >
          ${translateFn('Show less')}
        </gz-button>
      </gz-labeled-content>
      <div class="row">
        <gz-button
          id="view-related-artifacts"
          type="${ButtonTypes.OUTLINE}"
          buttonHeight=${ButtonHeights.SMALL}
          lowercase="true"
          @click="${this.dispatchFilterRelatedArtifact.bind(this)}"
        >
          ${translateFn('View related artifacts')}
        </gz-button>
      </div>`;
  }

  /**
   * Renders the artifacts section.
   * @returns The artifacts section template or nothing.
   */
  private renderArtifactsSection(): TemplateResult | symbol {
    if (!this.payload.groupedByType || this.payload.class === FindingClassType.ARTIFACT) {
      return html`${nothing}`;
    }

    const relatedArtifactsItems = [] as TemplateResult[];
    const relatedArtifactsObjects = this.payload.groupedByType.artifacts;
    for (
      let indexOfItem = 0;
      indexOfItem < this.numberOfArtifactsDisplayed && indexOfItem < relatedArtifactsObjects.length;
      indexOfItem++
    ) {
      relatedArtifactsItems.push(
        this.createRelatedFindingRow(relatedArtifactsObjects[indexOfItem], FindingClassType.ARTIFACT)
      );
    }

    const total = relatedArtifactsObjects.reduce((acc, item) => acc + item.items.length, 0);

    return html`
      <div class="section-title fs-11 bordered">${translateFn('Asset artifacts ({total})', { total: total })}</div>
      ${this.renderRelatedArtifactsBody(relatedArtifactsItems, relatedArtifactsObjects)}
    `;
  }

  /**
   * Creates a related finding row.
   * @param relatedFindings - The related findings group.
   * @param classType - The finding class type.
   * @returns The related finding row template.
   */
  private createRelatedFindingRow(relatedFindings: FindingGroup, classType: FindingClassType): TemplateResult {
    if (!relatedFindings) {
      return html`${nothing}`;
    }

    return FINDING_DETAILS_BY_TYPE[relatedFindings.type].getCategoryInfo(
      relatedFindings,
      classType,
      this.dispatchRelatedFindingGroupClick.bind(this)
    );
  }

  /**
   * Dispatches a related finding group click event.
   * @param classType - The finding class type.
   * @param findingType - The finding type.
   */
  private dispatchRelatedFindingGroupClick(classType: FindingClassType, findingType: FindingType | undefined): void {
    this.dispatchEvent(
      new CustomEvent(EasmSideMenuEvents.OPEN_FINDING_RELATED, {
        detail: {
          classType: classType,
          itemType: findingType,
          findingDetails: this.payload,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Handles selection change event.
   * @param event - The selection change event.
   */
  private onSelectionChanged(event: CustomEvent): void {
    switch (event.detail.selected) {
      case EASMActions.COPY_TO_CLIPBOARD_EMAIL:
        this.copyToClipboardEmail();
        break;
      case EASMActions.COPY_TO_CLIPBOARD_IP:
        this.copyToClipboardIp();
        break;
      case EASMActions.VIEW_INCIDENTS_IP:
        this.viewIncidentsIp();
        break;
      case EASMActions.VIEW_INCIDENTS_EMAIL:
        this.viewIncidentsEmail();
        break;
      default:
        throw new Error('Unknown action');
    }
  }

  /**
   * Copies email to clipboard.
   */
  private copyToClipboardEmail(): void {
    const emailType = this.payload.email as string;
    copyToClipboard(emailType);
  }

  /**
   * Copies IP to clipboard.
   */
  private copyToClipboardIp(): void {
    const ipType = this.payload.ip as string;
    copyToClipboard(ipType);
  }

  /**
   * Navigates to incidents by email.
   */
  private viewIncidentsEmail(): void {
    NavigationHelper.navigateToIncidents(this.dependencies.Ext, [
      {
        searchData: {
          complexSearch: {
            field: COMPLEX_SEARCH_FILTERS.EMAIL_ADDRESS,
            value: this.payload.email,
          },
        },
      },
    ]);
  }

  /**
   * Navigates to incidents area by IP.
   */
  private viewIncidentsIp(): void {
    NavigationHelper.navigateToIncidents(this.dependencies.Ext, [
      {
        searchData: {
          complexSearch: {
            field: COMPLEX_SEARCH_FILTERS.IP,
            value: this.payload.ip,
          },
        },
      },
    ]);
  }
}
